/**
 * Browser Sentry for the SPA — the port of `src/instrumentation-client.ts` onto
 * `@sentry/react`.
 *
 * Same DSN fallback, same privacy pipeline, same event filters, same sample
 * rates and the same tags. Two Next-only pieces are gone: Spotlight (a dev
 * overlay behind a Next-era flag) and the Page Router
 * `onRouterTransitionStart` hook, replaced here by Sentry's TanStack Router
 * tracing integration. Replay stays off (both sample rates 0), so the
 * credential-URL replay stop that hook carried has nothing left to stop.
 */
import * as Sentry from '@sentry/react'
import type { AnyRouter } from '@tanstack/react-router'

import {
  shouldDropCrossOriginStylesheetWarning,
  shouldDropDevPreviewEvent,
  shouldDropDocuSealThirdPartyError,
  shouldDropExtensionFocusWarning,
  shouldDropFido2ExtensionOnlyError,
  shouldDropFontLoadingNetworkError,
  shouldDropGtmStapeNetworkError,
  shouldDropKnownDomMutationNoise,
  shouldDropKnownMixpanelImplementationError,
  shouldDropMetaMaskExtensionError,
  shouldDropMetaPixelWarning,
  shouldDropNextJsClientExceptionWrapper,
  shouldDropNextJsRouteCancellationError,
  shouldDropSafariIOReadError,
  shouldDropTransientFetchNetworkError,
} from '@/utils/sentry/client-event-filters'
import {
  sanitizeSentryBreadcrumb,
  sanitizeSentryEvent,
  sanitizeSentrySpan,
  sanitizeSentryTransaction,
  SENTRY_PRIVACY_DATA_COLLECTION,
  type SanitizableSentryBreadcrumb,
  type SanitizableSentryEvent,
  type SanitizableSentrySpan,
  type SanitizableSentryTransaction,
} from '@/utils/sentry/privacy'
import { getBrowserProfilingConfig } from '@/utils/sentry/profiling-config'
import { sentryRelease } from '@/utils/sentry/release'
import { sentryTunnelPath } from '@/utils/sentry/tunnel-route'

const SENTRY_DSN =
  process.env.VITE_SENTRY_DSN ||
  'https://862b198203609f4ccdaf95a90de59626@o4507725944520704.ingest.us.sentry.io/4507732559200256'

// `next.config.js` set `tunnelRoute: '/monitoring'` and @sentry/nextjs turned it
// into this option, so browser events go out same-origin and an ad blocker
// cannot silence a user's error reports. `src/worker/monitoring.ts` is the other
// half; `undefined` (a non-SaaS DSN) means direct-to-ingest, as before.
const SENTRY_TUNNEL = sentryTunnelPath(SENTRY_DSN)

// Without this @sentry/react defaults every event to the literal "production",
// which would merge staging and production into one Sentry environment.
const SENTRY_ENVIRONMENT = process.env.VITE_APP_ENVIRONMENT ?? process.env.NODE_ENV ?? 'unknown'

// The Next config ran these as a straight-line chain of `if (…) return null`;
// the order is preserved so a rule change is a change to one list, not thirteen
// blocks.
const EVENT_FILTERS = [
  shouldDropKnownMixpanelImplementationError,
  shouldDropNextJsClientExceptionWrapper,
  shouldDropNextJsRouteCancellationError,
  shouldDropKnownDomMutationNoise,
  shouldDropGtmStapeNetworkError,
  shouldDropMetaPixelWarning,
  shouldDropTransientFetchNetworkError,
  shouldDropDocuSealThirdPartyError,
  shouldDropSafariIOReadError,
  shouldDropCrossOriginStylesheetWarning,
  shouldDropFontLoadingNetworkError,
  shouldDropMetaMaskExtensionError,
  shouldDropFido2ExtensionOnlyError,
  shouldDropExtensionFocusWarning,
  shouldDropDevPreviewEvent,
]

// A browser extension injects `/.webmcp/bridge.js` into the page; it POSTs
// `/mcp` on the app host, gets a 405 from Static Assets and logs the handled
// error with `console.warn`, which the console integration forwarded
// (NONRESIDENTTAX-FB, -F8). Nothing in this app serves `/.webmcp/`.
export const EXTENSION_BRIDGE_DENY_URLS: RegExp[] = [/\/\.webmcp\//]
export const EXTENSION_BRIDGE_IGNORE_ERRORS: RegExp[] = [/\[webmcp-interceptor\]/, /MCP endpoint returned HTTP/]

/**
 * An automated browser (agent-browser, Playwright) outside production.
 *
 * Agents verifying UI on staging simulate failures on purpose — every API call
 * answered 500, a fetch aborted, a completion refused with a hand-written 422 —
 * to screenshot the error states. Sentry cannot tell a mocked response from a
 * real one, so each simulation opened staging issues nobody could act on
 * (NONRESIDENTTAX-EQ, -ES, -ER, -EX, -EW, -EY, -EV, -ET, -E9, -EP). Those
 * browsers set `navigator.webdriver`; a person's browser does not. Production
 * is left alone: a real customer never runs one, and a monitor that does is
 * seeing the live site.
 */
function isAutomatedNonProductionBrowser(): boolean {
  return SENTRY_ENVIRONMENT !== 'production' && typeof navigator !== 'undefined' && navigator.webdriver === true
}

/**
 * Sentry's fetch transport (`makeFetchTransport`) reads the status and the
 * back-off headers and never the body. Chrome keeps an unread response body
 * open, so every envelope POST to `/monitoring` stayed "pending" for the life
 * of the page: DevTools never saw it finish and Playwright's `networkidle`
 * never came (perf follow-ups 2026-10-08, item 3; the tunnel itself answers in
 * ~0.4s). Draining the few bytes ingest returns (`{}` or `{"id":…}`) ends the
 * request. A failed read is ignored: the status and headers are already in.
 */
export function drainingFetch(fetchImpl: typeof fetch): typeof fetch {
  return async (input, init) => {
    const response = await fetchImpl(input, init)
    await response.arrayBuffer().catch(() => undefined)
    return response
  }
}

/**
 * The page's own `fetch`, taken before `Sentry.init` instruments it, so the
 * transport's posts never become breadcrumbs or spans of their own.
 * (Sentry's default takes a pristine `fetch` from a throwaway iframe; this
 * app does not patch `fetch` outside the /dev/ preview pages.)
 */
function sentryTransport(options: Parameters<typeof Sentry.makeFetchTransport>[0]) {
  // No fetch at all (an old embedded browser, a test DOM): the SDK's own default.
  const pageFetch = typeof window.fetch === 'function' ? drainingFetch(window.fetch.bind(window)) : undefined
  return Sentry.makeFetchTransport(options, pageFetch)
}

let initialised = false
/** True once `Sentry.init` has run: `reportError` and friends stop queueing. */
let clientReady = false

/**
 * `overrides` exists for the tests (a capturing transport, a sample rate);
 * the app passes none.
 */
export function initBrowserSentry(router: AnyRouter, overrides: Partial<Sentry.BrowserOptions> = {}): void {
  if (initialised) return
  initialised = true

  // The Next client config passed `process.env`, which the browser bundle
  // replaced with an empty object, so every profiling read resolved to its
  // default. Vite's define map does the same (`process.env` → `{}`); passing
  // `{}` explicitly keeps that behaviour visible instead of implied.
  const profilingConfig = getBrowserProfilingConfig({} as NodeJS.ProcessEnv)
  const integrations = [
    Sentry.captureConsoleIntegration({ levels: ['warn', 'error'] }),
    Sentry.tanstackRouterBrowserTracingIntegration(router),
    // Conditional spread keeps the array heterogeneous without naming the
    // Integration type, exactly as the Next config did.
    ...(profilingConfig.enableBrowserProfiling && profilingConfig.profileSessionSampleRate > 0
      ? [Sentry.browserProfilingIntegration()]
      : []),
  ]

  Sentry.init({
    dsn: SENTRY_DSN,
    tunnel: SENTRY_TUNNEL,
    transport: sentryTransport,
    environment: SENTRY_ENVIRONMENT,
    // `typeof` guard: the define is absent under Jest, where a bare reference throws.
    release: sentryRelease(typeof __COMMIT_SHA__ === 'string' ? __COMMIT_SHA__ : undefined),

    // Filter out form validation errors — these are expected user input errors,
    // not bugs.
    beforeSend(event, hint) {
      if (isAutomatedNonProductionBrowser()) return null

      for (const drop of EVENT_FILTERS) {
        if (drop(event as Parameters<typeof drop>[0])) return null
      }

      // Don't send ZodError validation errors from form validation to Sentry.
      const error = hint.originalException
      if (error && typeof error === 'object' && 'name' in error && (error as { name?: unknown }).name === 'ZodError') {
        return null
      }

      return sanitizeSentryEvent(event as typeof event & SanitizableSentryEvent)
    },

    beforeBreadcrumb(breadcrumb) {
      return sanitizeSentryBreadcrumb(breadcrumb as typeof breadcrumb & SanitizableSentryBreadcrumb)
    },

    // Ignore form validation error patterns
    ignoreErrors: [
      /ZodError/,
      /Please enter a valid ZIP code/,
      /Please select your state/,
      /Please enter a valid email/,
      /Invalid email/,
      /Load failed \(t\.nonresident\.tax\)/, // GTM Stape proxy network failures — third-party, not actionable
      /\[Meta Pixel\]/, // Meta Pixel validation warnings — handled in GTM, not actionable
      ...EXTENSION_BRIDGE_IGNORE_ERRORS,
    ],
    denyUrls: EXTENSION_BRIDGE_DENY_URLS,

    tracesSampleRate: profilingConfig.tracesSampleRate,
    profileSessionSampleRate: profilingConfig.profileSessionSampleRate,
    profileLifecycle: profilingConfig.profileLifecycle,
    // v11 streams spans by default, which makes beforeSendTransaction a no-op;
    // the privacy scrubbing below needs the static transaction model.
    traceLifecycle: 'static',
    dataCollection: SENTRY_PRIVACY_DATA_COLLECTION,

    debug: false,
    replaysOnErrorSampleRate: 0,
    replaysSessionSampleRate: 0,

    integrations,

    // Filter events to ignore known non-issues
    beforeSendTransaction(event) {
      if (event.transaction?.includes('/api/health')) return null
      return sanitizeSentryTransaction(event as typeof event & SanitizableSentryTransaction)
    },

    beforeSendSpan: Sentry.withStaticSpan((span) => sanitizeSentrySpan(span as typeof span & SanitizableSentrySpan)),

    ...overrides,
  })

  Sentry.setTag('runtime', 'client')
  Sentry.setTag('app', 'product')
  Sentry.setTag('app_env', SENTRY_ENVIRONMENT)

  clientReady = true
  flushEarlyReports()
}

/** Tags, extra, level… — the capture-context object every caller passes. */
export type ReportContext = Exclude<Sentry.CaptureContext, Sentry.Scope | ((scope: Sentry.Scope) => Sentry.Scope)>

/** At most this many pre-init reports are kept; a crash loop before init must not grow memory. */
const MAX_EARLY_REPORTS = 20

type EarlyReport =
  | { kind: 'exception'; error: unknown; hint: Sentry.EventHint }
  | { kind: 'message'; message: string; hint: Sentry.EventHint; level?: Sentry.SeverityLevel }
  | { kind: 'breadcrumb'; breadcrumb: Sentry.Breadcrumb }

const earlyReports: EarlyReport[] = []

function keepEarly(report: EarlyReport): void {
  if (earlyReports.length < MAX_EARLY_REPORTS) earlyReports.push(report)
}

/** A Sentry event id (32 hex), handed out before the client exists so callers can still show a reference. */
function newEventId(): string {
  const cryptoApi = globalThis.crypto
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') return cryptoApi.randomUUID().replace(/-/g, '')
  let id = ''
  for (let i = 0; i < 32; i++) id += Math.floor(Math.random() * 16).toString(16)
  return id
}

/** In order: a breadcrumb queued before an error is on that error. */
function flushEarlyReports(): void {
  for (const report of earlyReports.splice(0)) {
    try {
      replayEarlyReport(report)
    } catch {
      // One bad report must not cost the rest of the queue.
    }
  }
}

function replayEarlyReport(report: EarlyReport): void {
  if (report.kind === 'breadcrumb') Sentry.addBreadcrumb(report.breadcrumb)
  // A hint (event_id, mechanism, captureContext) passes through captureException
  // as is; captureMessage would wrap it as capture context, so messages go
  // through the scope to keep their event id.
  else if (report.kind === 'exception') Sentry.captureException(report.error, report.hint)
  else Sentry.getCurrentScope().captureMessage(report.message, report.level, report.hint)
}

/**
 * The app's one way to report an error (`Sentry.captureException`). Before the
 * deferred `Sentry.init` has run the report is queued with its own capture
 * context (scope tags and contexts are read when it is sent) and sent right
 * after init, under the event id returned here, so a reference shown to the
 * customer still finds the event.
 */
export function reportError(error: unknown, context?: ReportContext): string {
  if (clientReady) return Sentry.captureException(error, context)
  const eventId = newEventId()
  keepEarly({ kind: 'exception', error, hint: { event_id: eventId, captureContext: context } })
  return eventId
}

/** `Sentry.captureMessage`, queued like `reportError` before init. */
export function reportMessage(message: string, context?: ReportContext): string {
  if (clientReady) return Sentry.captureMessage(message, context)
  const eventId = newEventId()
  const level = context && 'level' in context ? context.level : undefined
  keepEarly({ kind: 'message', message, level, hint: { event_id: eventId, captureContext: context } })
  return eventId
}

/** `Sentry.addBreadcrumb`, which drops a breadcrumb outright while no client exists. */
export function addBreadcrumb(breadcrumb: Sentry.Breadcrumb): void {
  if (clientReady) Sentry.addBreadcrumb(breadcrumb)
  // Stamped now: replayed at init, it would otherwise carry the init time.
  else keepEarly({ kind: 'breadcrumb', breadcrumb: { timestamp: Date.now() / 1000, ...breadcrumb } })
}

// Scope writes and spans work before init (scope data persists into the
// client; a span without a client is a no-op), so they pass straight through.
export { setContext, setTag, setTags, setUser, startInactiveSpan, startSpan } from '@sentry/react'
export type { Breadcrumb, SeverityLevel, Span } from '@sentry/react'

type ScheduleTask = (task: () => void) => void

/**
 * After first paint: on the first idle slot, at most 2s later. Safari has no
 * `requestIdleCallback`; there the frame after the next paint stands in.
 */
export function scheduleAfterFirstPaint(task: () => void): void {
  const idle = (window as Window & { requestIdleCallback?: Window['requestIdleCallback'] }).requestIdleCallback
  if (typeof idle === 'function') {
    idle.call(window, () => task(), { timeout: 2000 })
    return
  }
  const nextFrame =
    typeof window.requestAnimationFrame === 'function' ? window.requestAnimationFrame.bind(window) : null
  if (nextFrame) nextFrame(() => setTimeout(task, 0))
  else setTimeout(task, 0)
}

/**
 * `initBrowserSentry`, off the first render's critical path (perf spec
 * 2026-10-08, stream B). Until the client exists, Sentry's own global
 * handlers are not installed, so errors and unhandled rejections thrown in
 * that window are queued — with every `reportError`/`reportMessage`/
 * `addBreadcrumb` made meanwhile — and sent, in order, right after init.
 */
export function deferBrowserSentry(
  router: AnyRouter,
  schedule: ScheduleTask = scheduleAfterFirstPaint,
  overrides: Partial<Sentry.BrowserOptions> = {},
): void {
  if (typeof window === 'undefined') {
    initBrowserSentry(router, overrides)
    return
  }
  const keepUncaught = (error: unknown, type: string) =>
    keepEarly({
      kind: 'exception',
      error,
      hint: { mechanism: { type: `auto.browser.deferred_init.${type}`, handled: false } },
    })
  const onError = (event: ErrorEvent) => keepUncaught(event.error ?? new Error(event.message), 'onerror')
  const onRejection = (event: PromiseRejectionEvent) => keepUncaught(event.reason, 'onunhandledrejection')
  window.addEventListener('error', onError)
  window.addEventListener('unhandledrejection', onRejection)

  schedule(() => {
    window.removeEventListener('error', onError)
    window.removeEventListener('unhandledrejection', onRejection)
    initBrowserSentry(router, overrides)
  })
}
